import Axios from 'api/client';
import { AxiosRequestConfig } from 'axios';
import {
    CustomFieldGroup,
    FilterCampaign,
    FilterCampaignMatch,
    FilterCustomField,
    FilterExternalLabel,
    FilterFund,
    FilterMailing,
    FilterTag,
    FilterTeam,
    FilterTeamMember,
    FilterTicketType,
    FilterTrackableMessage,
    FilterWidget,
} from 'types/domain';

type FilterResourceScope = 'contact';

interface FetchFilterTagsParams {
    accountId: number;
    scope: FilterResourceScope;
}

type FilterExternalLabelTypeScope = 'transactions';

interface FetchFilterExternalLabelsParams {
    accountId: number;
    scope: FilterExternalLabelTypeScope;
}

interface FetchFilterCustomFieldsParams {
    accountId: number;
    scope: CustomFieldGroup;
}

interface FilterParams extends AxiosRequestConfig {
    accountId: number;
}

const fetchFilterResource = <T>(resource: string) => {
    return ({ accountId, ...params }: FilterParams) =>
        Axios.get<T[]>(`/accounts/${accountId}/filters/${resource}`, params).then(
            response => response.data,
        );
};

const fetchFilterTeams = fetchFilterResource<FilterTeam>('teams');
const fetchFilterTeamMembers = fetchFilterResource<FilterTeamMember>('team-members');
const fetchFilterCampaigns = fetchFilterResource<FilterCampaign>('campaigns');
const fetchFilterFunds = fetchFilterResource<FilterFund>('funds');
const fetchFilterTicketTypes = fetchFilterResource<FilterTicketType>('ticket-types');

const filterTagRequest = fetchFilterResource<FilterTag>('tags');
const fetchFilterTags = ({ accountId, scope }: FetchFilterTagsParams) =>
    filterTagRequest({ accountId, params: { scope } });

const filterExternalLabelsRequest = fetchFilterResource<FilterExternalLabel>('external-labels');
const fetchFilterExternalLabels = ({ accountId, scope }: FetchFilterExternalLabelsParams) =>
    filterExternalLabelsRequest({ accountId, params: { scope } });

const fetchFilterCampaignMatches = fetchFilterResource<FilterCampaignMatch>('campaign-matches');

const fetchFilterWidgets = fetchFilterResource<FilterWidget>('widgets');

const filterCustomFieldsRequest = fetchFilterResource<FilterCustomField>('custom-fields');
const fetchFilterCustomFields = ({ accountId, scope }: FetchFilterCustomFieldsParams) =>
    filterCustomFieldsRequest({ accountId, params: { scope } });

const fetchFilterTrackableMessages =
    fetchFilterResource<FilterTrackableMessage>('trackable-messages');

const fetchFilterMailings = fetchFilterResource<FilterMailing>('mailings');

export type { FilterResourceScope, FilterExternalLabelTypeScope, FetchFilterCustomFieldsParams };
export {
    fetchFilterResource,
    fetchFilterTeams,
    fetchFilterTeamMembers,
    fetchFilterCampaigns,
    fetchFilterFunds,
    fetchFilterTicketTypes,
    fetchFilterTags,
    fetchFilterExternalLabels,
    fetchFilterCampaignMatches,
    fetchFilterCustomFields,
    fetchFilterWidgets,
    fetchFilterTrackableMessages,
    fetchFilterMailings,
};
